
import { WorldCountry } from './types';

// Sistema de protección de países crítico
const PROTECTED_COUNTRY_COUNT = 152; // Aumentado de 108 a 152 tras expansiones
const MINIMUM_CITIES_PER_COUNTRY = 10;
const MAXIMUM_ALLOWED_COUNTRIES = 200; // Límite máximo para evitar sobrecarga

export interface ProtectionResult {
  success: boolean;
  message: string;
  currentCount: number;
  protectedCount: number;
}

export interface ProtectionStats {
  currentCount: number;
  protectedCount: number;
  totalCities: number;
  citiesPerCountry: number;
  status: 'PROTECTED' | 'WARNING' | 'CRITICAL';
  protectionLevel: string;
  isProtected: boolean;
}

export const protectCountryCount = (countries: WorldCountry[]): ProtectionResult => {
  const currentCount = countries.length;
  
  if (currentCount < PROTECTED_COUNTRY_COUNT) {
    return {
      success: false,
      message: `🚨 CRITICAL: Solo ${currentCount} países disponibles. Se requieren mínimo ${PROTECTED_COUNTRY_COUNT} países.`,
      currentCount,
      protectedCount: PROTECTED_COUNTRY_COUNT
    };
  }
  
  if (currentCount > MAXIMUM_ALLOWED_COUNTRIES) {
    return {
      success: false,
      message: `⚠️ WARNING: ${currentCount} países excede el límite máximo de ${MAXIMUM_ALLOWED_COUNTRIES}. Considere optimización.`,
      currentCount,
      protectedCount: PROTECTED_COUNTRY_COUNT
    };
  }
  
  return {
    success: true,
    message: `✅ PROTECTION ACTIVE: ${currentCount} países disponibles (objetivo: ${PROTECTED_COUNTRY_COUNT}+)`,
    currentCount,
    protectedCount: PROTECTED_COUNTRY_COUNT
  };
};

export const validateProtectedCount = (countries: WorldCountry[]): void => {
  const result = protectCountryCount(countries);
  
  if (!result.success && result.currentCount < PROTECTED_COUNTRY_COUNT) {
    console.error(`🛡️ COUNTRY PROTECTION FAILED: ${result.message}`);
    console.error(`📊 Current: ${result.currentCount}, Required: ${result.protectedCount}`);
    
    // En desarrollo, mostrar advertencia crítica
    if (process.env.NODE_ENV === 'development') {
      console.warn('🔧 PHASE 12 EXPANSION STATUS:');
      console.warn('  ✅ África: Marruecos 100+ ciudades, Túnez y Sudán expandidos');
      console.warn('  ✅ Asia: Singapur 100+ ciudades, Laos y Camboya agregados');
      console.warn('  ✅ América: Jamaica, Trinidad y Tobago, Barbados, Bahamas expandidos');
      console.warn('  ✅ Europa: Estonia, Letonia y Moldavia expandidas');
      console.warn('  ✅ Oceanía: Samoa, Tonga y Vanuatu agregados');
    }
  }
};

export const getProtectionStats = (countries: WorldCountry[]): ProtectionStats => {
  const currentCount = countries.length;
  const totalCities = countries.reduce((total, country) => total + country.topCities.length, 0);
  const citiesPerCountry = Math.round(totalCities / currentCount);
  
  let status: 'PROTECTED' | 'WARNING' | 'CRITICAL';
  let protectionLevel: string;
  
  if (currentCount >= PROTECTED_COUNTRY_COUNT) {
    status = 'PROTECTED';
    protectionLevel = `FASE 12 COMPLETA (${currentCount}/${PROTECTED_COUNTRY_COUNT})`;
  } else if (currentCount >= PROTECTED_COUNTRY_COUNT * 0.9) {
    status = 'WARNING';
    protectionLevel = `NEAR COMPLETION (${currentCount}/${PROTECTED_COUNTRY_COUNT})`;
  } else {
    status = 'CRITICAL';
    protectionLevel = `NEEDS EXPANSION (${currentCount}/${PROTECTED_COUNTRY_COUNT})`;
  }
  
  return {
    currentCount,
    protectedCount: PROTECTED_COUNTRY_COUNT,
    totalCities,
    citiesPerCountry,
    status,
    protectionLevel,
    isProtected: currentCount >= PROTECTED_COUNTRY_COUNT
  };
};

// Verificación de integridad de datos
export const validateCountryIntegrity = (countries: WorldCountry[]): void => {
  const duplicateSlugs = new Set();
  const duplicates: string[] = [];
  
  countries.forEach(country => {
    if (duplicateSlugs.has(country.slug)) {
      duplicates.push(country.slug);
    }
    duplicateSlugs.add(country.slug);
    
    // Verificar que cada país tenga al menos el mínimo de ciudades
    if (country.topCities.length < MINIMUM_CITIES_PER_COUNTRY) {
      console.warn(`⚠️ ${country.name} tiene solo ${country.topCities.length} ciudades (mínimo: ${MINIMUM_CITIES_PER_COUNTRY})`);
    }
  });
  
  if (duplicates.length > 0) {
    console.error(`🚨 DUPLICATED COUNTRY SLUGS DETECTED: ${duplicates.join(', ')}`);
  }
  
  console.log(`🔍 Integrity Check: ${countries.length} países validados`);
};
